import {
  SlotMeta,
  DcaNode,
  CfNode,
  SlotContent,
  DcaSlotTarget,
  ExtendedCfNode,
} from '../organisms/DynamicTabbedLayout/DynamicTabbedLayout.types';
import { isRecord } from './slotPropNormalizerUtils';

// Pattern builder — defines grid structure (banner/sixpack combos)
export const buildPatternSlots = (bannerFirst: boolean, squareFirst: boolean, dcaNodes: DcaNode[] = []): SlotMeta[] => {
  const row1 = squareFirst ? ['square', 'tall', 'square'] : ['tall', 'square', 'tall'];
  const row2 = squareFirst ? ['tall', 'square', 'tall'] : ['square', 'tall', 'square'];
  const sixpack = [...row1, ...row2];

  // Prepare authored DCA orders for fast lookup
  const dcaOrders = new Set<number>();
  for (let i = 0; i < (dcaNodes?.length || 0); i++) {
    const num = Number(dcaNodes[i]?.order);
    if (!Number.isNaN(num)) dcaOrders.add(num);
  }

  const typeForPosition = (position: number): 'dca' | 'cf' => (dcaOrders.has(position) ? 'dca' : 'cf');

  const mapSixpack = (start: number): SlotMeta[] =>
    sixpack.map((size, i) => {
      const position = start + i;
      return {
        size: size as 'square' | 'tall',
        position,
        type: typeForPosition(position),
      };
    });

  if (bannerFirst) {
    const firstSixStart = 2;
    const secondSixStart = 9;
    return [
      { size: 'banner', position: 1, type: typeForPosition(1) },
      ...mapSixpack(firstSixStart),
      { size: 'banner', position: 8, type: typeForPosition(8) },
      ...mapSixpack(secondSixStart),
    ];
  }

  const secondSixStart = 8;
  return [...mapSixpack(1), { size: 'banner', position: 7, type: typeForPosition(7) }, ...mapSixpack(secondSixStart)];
};

export function mergeCfAndDcaToSlots(
  slotsMeta: SlotMeta[],
  dcaNodes: DcaNode[],
  cfData: CfNode[]
): SlotContent[] | null {
  const len = slotsMeta?.length;
  const result = new Array(len);

  // Build a fast lookup map: order → DCA node
  const dcaByOrder = new Map<number, DcaNode>();
  for (let i = 0; i < dcaNodes.length; i++) {
    const order = dcaNodes[i].order;
    if (order !== undefined && order !== null) {
      const num = Number(order);
      if (!Number.isNaN(num)) dcaByOrder.set(num, dcaNodes[i]);
    }
  }

  // DCA slots consume a CF item as their fallback; if DCA payload is empty/failed the CF content renders.
  for (let i = 0; i < len; i++) {
    const slot = slotsMeta[i];
    const position = typeof slot?.position === 'number' ? slot.position : i;
    const authoredDca = dcaByOrder.get(position);

    if (authoredDca) {
      result[i] = {
        ...slot,
        position,
        type: 'dca',
        payload: null,
        cfFallback: cfData[i] ?? null,
        dcaTarget: {
          order: authoredDca.order,
          mboxParameter: authoredDca.mboxParameter,
        },
      };
    } else {
      result[i] = {
        ...slot,
        position,
        type: 'cf',
        payload: cfData[i] ?? null,
        cfFallback: null,
      };
    }
  }
  return result;
}

/** Mango UXL assets must include at least one non-empty primary image URL (matches RiottMediaCard / helpers). */
const MANGO_PRIMARY_IMAGE_KEYS = ['primaryImage_desktop', 'primaryImage_mobile', 'primaryImage_tablet'] as const;

const CACHE_IMAGE_DOMAIN = 'https://cache.marriott.com';

type RecentlyViewedImageVariant =
  | 'square'
  | 'rectangle'
  | 'standardHorizontal'
  | 'panoramicHorizontal'
  | 'classicVertical';

const RECENTLY_VIEWED_RENDITION_BY_VARIANT: Record<RecentlyViewedImageVariant, string> = {
  square: 'Square',
  rectangle: 'Wide-Hor',
  standardHorizontal: 'Standard-Hor',
  panoramicHorizontal: 'Pano-Hor',
  classicVertical: 'Classic-Ver',
};

function hasNonEmptyMangoPrimaryImageFields(assets: Record<string, unknown>): boolean {
  for (const key of MANGO_PRIMARY_IMAGE_KEYS) {
    const v = assets[key];
    if (typeof v === 'string' && v.trim() !== '') {
      return true;
    }
  }
  return false;
}

function toAbsoluteCacheUrl(pathOrUrl: string): string {
  const value = (pathOrUrl || '').trim();
  if (!value) return '';
  if (/^https?:\/\//i.test(value)) return value;
  return value.startsWith('/') ? `${CACHE_IMAGE_DOMAIN}${value}` : value;
}

function toRecentlyViewedVariantUrl(pathOrUrl: string, variant: RecentlyViewedImageVariant): string {
  const absolute = toAbsoluteCacheUrl(pathOrUrl);
  if (!absolute) return '';

  const base = absolute.split('?')[0] ?? '';
  if (!base.includes('/is/image/')) {
    return absolute;
  }

  const lastSlash = base.lastIndexOf('/');
  const lastColon = base.lastIndexOf(':');
  const rendition = RECENTLY_VIEWED_RENDITION_BY_VARIANT[variant];
  const variantBase = lastColon > lastSlash ? `${base.slice(0, lastColon)}:${rendition}` : `${base}:${rendition}`;

  return variantBase;
}

export function getRecentlyViewedImageFromLocalStorage(variant: RecentlyViewedImageVariant): string {
  if (typeof window === 'undefined') return '';
  const stored = window.localStorage.getItem('miRecentlyViewedProperties');
  if (!stored) return '';

  try {
    const rv = JSON.parse(stored)?.recentlyViewedProperties?.[0] ?? {};
    const imageSrc = typeof rv?.imageSrc === 'string' ? rv.imageSrc : '';
    const imageUrl = typeof rv?.imageUrl === 'string' ? rv.imageUrl : '';

    const candidate = imageSrc && imageSrc?.indexOf('.tif') === -1 ? imageSrc : imageUrl;
    if (!candidate) return '';
    return toRecentlyViewedVariantUrl(candidate, variant);
  } catch {
    return '';
  }
}

export function hasRecentlyViewedMetaCue(metaData: Record<string, unknown> | undefined): boolean {
  if (!metaData) return false;

  if (metaData['isRecentlyViewed'] === true || metaData['isRecentlyViewed'] === 'true') {
    return true;
  }
  return false;
}

export function getRecentlyViewedImageFallbackForPayload(
  payload: Record<string, unknown>,
  variant: RecentlyViewedImageVariant
): string {
  const rawMeta = payload['metaData'] ?? payload['metadata'];
  const metaData = isRecord(rawMeta) ? rawMeta : undefined;
  if (!hasRecentlyViewedMetaCue(metaData)) return '';

  return getRecentlyViewedImageFromLocalStorage(variant);
}

function hydrateRecentlyViewedMangoPrimaryImages(payload: Record<string, unknown>): boolean {
  const rawMeta = payload['metaData'] ?? payload['metadata'];
  const metaData = isRecord(rawMeta) ? rawMeta : undefined;
  if (!hasRecentlyViewedMetaCue(metaData)) return false;

  const adData = payload['adData'];
  const firstAd = Array.isArray(adData) ? adData[0] : undefined;
  if (!isRecord(firstAd)) return false;

  let assetsRecord: Record<string, unknown>;
  if (isRecord(firstAd['assets'])) {
    assetsRecord = firstAd['assets'];
  } else {
    assetsRecord = {};
    firstAd['assets'] = assetsRecord;
  }

  if (hasNonEmptyMangoPrimaryImageFields(assetsRecord)) {
    return true;
  }

  const squareImageUrl = getRecentlyViewedImageFromLocalStorage('square');
  if (!squareImageUrl) return false;

  assetsRecord['primaryImage_desktop'] = squareImageUrl;
  assetsRecord['primaryImage_tablet'] = squareImageUrl;
  assetsRecord['primaryImage_mobile'] = squareImageUrl;
  return true;
}

/** UXL/Target-shaped payloads: not usable as DCA when not loaded or error (cfFallback should show). */
export function isDcaTargetPayloadFailed(payload: unknown): boolean {
  if (!isRecord(payload)) return true;
  const p = payload;
  if (p['isTargetContentLoaded'] === false) return true;
  if (p['isTargetContentError'] === true) return true;
  if (p['loadCfData'] === true) return true;

  // error case when isMango is true but payload has no assets
  const rawMeta = p['metaData'] ?? p['metadata'];
  const metaData = isRecord(rawMeta) ? rawMeta : undefined;
  if (metaData?.['isMango'] === true) {
    const adData = p['adData'];
    const firstAd = Array.isArray(adData) ? adData[0] : undefined;
    const nestedAssets = isRecord(firstAd) ? firstAd['assets'] : undefined;
    const hasPrimaryImages = isRecord(nestedAssets) && hasNonEmptyMangoPrimaryImageFields(nestedAssets);

    if (!hasPrimaryImages && hasRecentlyViewedMetaCue(metaData)) {
      const hydrated = hydrateRecentlyViewedMangoPrimaryImages(p);
      if (!hydrated) {
        return true;
      }
    } else if (!hasPrimaryImages) {
      return true;
    }
  }
  return false;
}

/**
 * Stored when Alloy returned no row for this mbox, the HTTP/fetch failed, or the response was not an array.
 * Treated as failed by {@link isDcaTargetPayloadFailed} so overlay can fall back to CF after DCA has responded.
 * `fromUxlPageModel: false` indicates this was a fetch was from getDCAdata failure in DTL — should retry.
 */
export const DCA_MBOX_RESPONSE_FAILED: Record<string, unknown> = {
  isTargetContentLoaded: true,
  isTargetContentError: true,
  fromUxlPageModel: false,
};

/**
 * Stored when Alloy returned a successful array response but contained no row for this mbox
 * (i.e. no data AND no error). Treated as "resolved" so {@link getDcaData} will not re-fetch,
 * but overlay will fall back to CF content via the `loadCfData` flag.
 */
export const DCA_MBOX_RESPONSE_EMPTY: Record<string, unknown> = {
  isTargetContentLoaded: true,
  isTargetContentError: false,
  loadCfData: true,
};

/**
 * Resolves a list of mbox response items against a set of expected mbox names.
 * - Items with `isTargetContentError: true` → {@link DCA_MBOX_RESPONSE_FAILED}
 * - Items with valid `uxlResponse` → stored as-is
 * - Expected mbox names missing from the response → {@link DCA_MBOX_RESPONSE_EMPTY}
 *
 * Used by both the initial-load seeding (from useDCADynamicModel's pageModel.mboxData)
 * and the client-side Alloy fetch path ({@link mergeAlloyMboxResponses}).
 */
export function resolveMboxResponses(
  mboxItems: Record<string, unknown>[],
  expectedMboxNames: string[]
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const seen = new Set<string>();

  for (const item of mboxItems) {
    const key = String(item?.['mboxName'] ?? '');
    if (!key) continue;
    seen.add(key);
    // Response from pageModel contains uxlResponse
    const fromUxlPageModel = Boolean((item as Record<string, unknown>)?.['uxlResponse']);
    const resolved = (item as Record<string, unknown>)?.['uxlResponse'] ?? item;

    if (isRecord(resolved) && resolved['isTargetContentError'] === true) {
      // Mark whether error came from pageModel (don't retry) or getDCAdata fetch failure (retry)
      out[key] = { ...DCA_MBOX_RESPONSE_FAILED, fromUxlPageModel };
    } else {
      out[key] = resolved;
    }
  }

  for (const name of expectedMboxNames) {
    if (name && !seen.has(name)) {
      out[name] = { ...DCA_MBOX_RESPONSE_EMPTY };
    }
  }

  return out;
}

/**
 * Normalizes an Alloy `fetchDCADynamicModelForAlloy` outcome into per-mbox store entries.
 * Requested mboxes missing from a successful array response receive {@link DCA_MBOX_RESPONSE_EMPTY}
 * so slots can fall back to CF without staying blank (partial / empty success).
 */
export function mergeAlloyMboxResponses(
  payloadMboxes: { mboxName?: string }[],
  response: unknown
): Record<string, unknown> {
  const requestedNames = payloadMboxes.map(item => String(item?.mboxName ?? '')).filter((n): n is string => Boolean(n));

  if (!Array.isArray(response)) {
    const out: Record<string, unknown> = {};
    for (const name of requestedNames) {
      out[name] = { ...DCA_MBOX_RESPONSE_FAILED };
    }
    return out;
  }

  return resolveMboxResponses(response as Record<string, unknown>[], requestedNames);
}

function getDcaFallbackTarget(slot: SlotContent, match?: DcaNode): DcaSlotTarget | undefined {
  const order = match?.order ?? slot.dcaTarget?.order;
  const mboxParameter = match?.mboxParameter ?? slot.dcaTarget?.mboxParameter;
  if (order === undefined && !mboxParameter) return undefined;
  return { order, mboxParameter };
}

function applyCfFallback(slot: SlotContent, match?: DcaNode): SlotContent {
  return {
    ...slot,
    payload: slot.cfFallback,
    type: 'cf',
    cfFallback: null,
    dcaFallbackTarget: getDcaFallbackTarget(slot, match),
  };
}

// Overlay map onto slots (returns new slots array)
export function overlaySlotsWithDcaRes(
  slots: SlotContent[] = [],
  dcaNodes: DcaNode[] = [],
  opts?: { fillFromCFFallbackIfEmpty?: boolean }
) {
  const hasDca = Array.isArray(dcaNodes) && dcaNodes.length > 0;

  if (!hasDca && opts?.fillFromCFFallbackIfEmpty) {
    const filled = (slots || []).map(s => {
      if (s?.type === 'dca' && s?.cfFallback) {
        return applyCfFallback(s);
      }
      return s;
    });
    return filled;
  }

  const overlaid = (slots || []).map(s => {
    const match = dcaNodes.find(n => n.order === (s as SlotContent)?.position);
    if (match) {
      const payload = match.payload;
      if (isDcaTargetPayloadFailed(payload)) {
        const slot = s as SlotContent;
        if (slot?.cfFallback) {
          return applyCfFallback(slot, match);
        }
        return { ...s, payload: null, type: 'dca' };
      }
      return { ...s, payload, type: 'dca' };
    }
    if (opts?.fillFromCFFallbackIfEmpty && s?.type === 'dca' && s?.cfFallback) {
      return applyCfFallback(s as SlotContent);
    }
    return s;
  });
  return overlaid;
}

// Normalizes raw AEM GraphQL items into the CF structure.
export function normalizeCfItems(nodes: ExtendedCfNode[], tabName: string) {
  if (!Array.isArray(nodes) || nodes.length === 0) return [];

  const count = nodes.length;
  const result: CfNode[] = new Array(count);

  for (let i = 0; i < count; i++) {
    const item = (nodes[i] ?? {}) as ExtendedCfNode;
    const icon = item.icon;
    const badgeIcon = item.badgeIcon;
    const brandLogoIcon = item.brandLogoIcon;

    const baseCard = {
      ...item,
      id: item.id || `${tabName}-${Math.random().toString(36).slice(2)}`,
      tabName,
      theme: item.theme,
      icon: icon && icon.length ? icon[0] : '',
      description: (item.description as { plaintext?: string })?.plaintext ?? '',
    };

    switch (item.cardType) {
      case 'ProductDetailsCardModel': {
        const tags = item.tagsList;
        let tagListItems: { tagLabel: string }[] = [];
        if (tags && tags.length > 0) {
          const tCount = tags.length;
          tagListItems = new Array(tCount);
          for (let t = 0; t < tCount; t++) {
            tagListItems[t] = { tagLabel: tags[t] };
          }
        }
        result[i] = {
          ...baseCard,
          badgeIcon: badgeIcon && badgeIcon.length ? badgeIcon[0] : '',
          brandLogoIcon: brandLogoIcon && brandLogoIcon.length ? brandLogoIcon[0] : '',
          tagListItems,
        } as CfNode;
        break;
      }
      case 'EnrollmentCardModel':
        result[i] = {
          ...baseCard,
        } as CfNode;
        break;
      case 'CoBrandCardModel':
      case 'LayeredFeatureCardModel':
      default:
        result[i] = baseCard;
        break;
    }
  }

  return result;
}
